// src/pages/BessPage.js
import { DashboardBessHtml } from '../../components/skins/dashboard-bess.ts';
import { globalState } from "../../framework/globalState.ts";
import { navigateTo } from '../../framework/router.ts';
import { subscribeRegister } from "../../services/mqttClient.ts";
import { fetchAlarms } from '../../services/api.ts';
import { RechartsChartManager } from "../../components/charts/RechartsChartManager.tsx";
import { chartStyles } from "../../components/charts/ChartStyles.ts";
import { groupByDictionaryKey, applyThreshold } from '../../services/registerUtils.ts';
import {
  clearThresholdIndicator,
  computeAggregateFromMap,
  getSummaryThresholdSeverity,
  type SummaryThresholdSpec,
} from '../../services/summaryThreshold.ts';

const BESS_PAGE_WARM_KEY_PREFIX = 'vetra.bess.pageWarm:';

export default function BessPage() {
  const container = document.createElement("div");
  container.classList.add("parent-container");
  container.classList.add("dashboard-bess-page");
  const bessskin = new DashboardBessHtml().bessskin();
  container.innerHTML = bessskin.trim();

  const siteWithData = globalState.currentSiteWithData as any;
  const site = siteWithData?.site || siteWithData;
  const registers = (siteWithData?.devices || []).flatMap((d: any) =>
        (d.registers || []).map(r => ({ ...r, deviceName: d.name, deviceId: d._id }))
      );

  const subscriptions: Array<() => void> = [];
  let hasShownInitialBessLoading = false;
  let hasClearedInitialBessLoading = false;
  let initialBessRevealTimers: number[] = [];
  let bessAlarmPollTimer: number | null = null;

  if (!site?._id) {
    container.innerHTML += "<p class='text-center text-danger'>No site selected.</p>";
    return container;
  }

  const bessWarmKey = `${BESS_PAGE_WARM_KEY_PREFIX}${String(site._id)}`;
  const hasWarmBessPage = sessionStorage.getItem(bessWarmKey) === '1';


  // DOM Cache
  const arrayTableBody = container.querySelector(".arrayBody");
  const remoteControlSection = container.querySelector('.container-column.remote-control');
  const bessAlarmsBody = container.querySelector('[data-bess-alarms-body]') as HTMLElement | null;
  const summaryElements = {
    soc: container.querySelector('[data-register="soc"]'),
    availablebanks: container.querySelector('[data-register="bessavailablebanks"]'),
    availablechargepower: container.querySelector('[data-register="bessavailablechargepower"]'),
    availabledischargepower: container.querySelector('[data-register="bessavailabledischargepower"]'),
    chargeenergy: container.querySelector('[data-register="besschargeenergy"]'),
    dischargeenergy: container.querySelector('[data-register="bessdischargeenergy"]'),
    chargeenergytoday: container.querySelector('[data-register="besschargeenergytoday"]'),
    dischargeenergytoday: container.querySelector('[data-register="bessdischargeenergytoday"]'),
    netenergy: container.querySelector('[data-register="bessnetenergy"]'),
    energythroughput: container.querySelector('[data-register="bessenergythroughput"]'),
    rtefficiency: container.querySelector('[data-register="bessrtefficiency"]'),
    bessalarms: container.querySelector('[data-register="bessalarms"]'),
  };

  const formatAlarmTime = (value: unknown) => {
    const date = value ? new Date(String(value)) : null;
    if (!date || Number.isNaN(date.getTime())) return '--:--:--';
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  };

  const isLikelyBessAlarm = (alarm: any) => {
    const category = String(alarm?.category || '').toLowerCase();
    const message = String(alarm?.message || alarm?.title || '').toLowerCase();
    const registerName = String(alarm?.registerName || '').toLowerCase();
    const haystack = `${category} ${message} ${registerName}`;

    const hasBessToken = /(bess|battery|pcs|soc|soh|rack|cell|charge|discharge)/i.test(haystack);
    const hasPvInverterToken = /(inverter|pv|solar|inv_)/i.test(haystack);

    // Must look like BESS context. Do not include inverter/PV-only alarms.
    if (!hasBessToken) return false;
    if (hasPvInverterToken && !/(bess|battery|pcs)/i.test(haystack)) return false;
    return true;
  };

  const renderBessAlarmRows = (alarms: any[]) => {
    if (!bessAlarmsBody) return;
    const list = Array.isArray(alarms) ? alarms.slice(0, 6) : [];

    if (!list.length) {
      bessAlarmsBody.innerHTML = '<tr><td colspan="4" class="bess-alarms-empty">No active BESS alarms</td></tr>';
      return;
    }

    bessAlarmsBody.innerHTML = list.map((alarm: any) => {
      const severity = String(alarm?.severity || '-').toLowerCase();
      const status = String(alarm?.state || alarm?.status || 'active').toLowerCase();
      const message = String(alarm?.message || alarm?.title || 'Alarm event');

      return `
        <tr class="alarm-row priority-${severity}">
          <td>${formatAlarmTime(alarm?.triggeredAt || alarm?.createdAt || alarm?.updatedAt)}</td>
          <td><span class="bess-alarm-severity severity-${severity}">${severity.toUpperCase()}</span></td>
          <td class="bess-alarm-message">${message}</td>
          <td><span class="bess-alarm-status status-${status}">${status.toUpperCase()}</span></td>
        </tr>
      `;
    }).join('');
  };

  const refreshBessAlarms = async () => {
    try {
      const categoryResult = await fetchAlarms({ siteId: site._id, state: 'active', category: 'bess', limit: 200 } as any);
      let alarms = (Array.isArray(categoryResult) ? categoryResult : []).filter(isLikelyBessAlarm);

      // Backward compatibility: if category filter yields nothing on some backends,
      // fallback to active site alarms and filter client-side for BESS-related entries.
      if (!alarms.length) {
        const siteActive = await fetchAlarms({ siteId: site._id, state: 'active', limit: 200 } as any);
        const siteAlarms = Array.isArray(siteActive) ? siteActive : [];
        alarms = siteAlarms.filter(isLikelyBessAlarm);
      }

      if (summaryElements.bessalarms) {
        (summaryElements.bessalarms as HTMLElement).textContent = String(alarms.length);
      }
      renderBessAlarmRows(alarms);
    } catch (error) {
      console.warn('Failed to refresh BESS alarms', error);
      if (summaryElements.bessalarms) {
        (summaryElements.bessalarms as HTMLElement).textContent = '0';
      }
      renderBessAlarmRows([]);
    }
  };

  if (!arrayTableBody) {
    console.error("Array table body not found.");
    return container;
  }

  const showInitialBessLoading = () => {
    if (hasShownInitialBessLoading) return;
    const shimmerTargets = [
      ...Array.from(container.querySelectorAll('.dashboard-panel')),
      ...Array.from(container.querySelectorAll('.bess-summary-list > div')),
    ];
    shimmerTargets.forEach((el) => el.classList.add('is-loading'));
    hasShownInitialBessLoading = true;
  };

  const clearInitialBessLoadingProgressively = () => {
    if (hasClearedInitialBessLoading) return;
    hasClearedInitialBessLoading = true;

    initialBessRevealTimers.forEach((timerId) => window.clearTimeout(timerId));
    initialBessRevealTimers = [];

    const revealGroups = [
      Array.from(container.querySelectorAll('.bess-summary-list > div')),
      Array.from(container.querySelectorAll('.dashboard-panel')),
    ];

    revealGroups.forEach((group, index) => {
      const timerId = window.setTimeout(() => {
        group.forEach((el) => el.classList.remove('is-loading'));
      }, index * 120);
      initialBessRevealTimers.push(timerId);
    });
  };

  /* ========================================
     BESS ARRAY (Per-BESS Table)
     ======================================== */
  const arrayGroup = groupByDictionaryKey(registers);

  const arrayMetricSources: Record<string, string[]> = {
    bess_aux_power: ['bess_aux_power', 'bess_active_power'],
    bess_reactive_power_meter: ['bess_reactive_power_meter', 'bess_reactive_power'],
    bess_energy_charge_year: ['bess_energy_charge_year', 'bess_charge_energy', 'bess_discharge_energy'],
    bess_energy_charge_day: ['bess_energy_charge_day', 'bess_charge_energy_day', 'bess_discharge_energy_day', 'bess_charge_energy'],
    bess_device_status: ['bess_device_status', 'bess_status'],
    bess_alarm_status: ['bess_alarm_status', 'bess_fault_status', 'bess_alarm'],
  };

  const resolveArrayCanonicalKey = (key: string): string | null => {
    for (const [canonical, aliases] of Object.entries(arrayMetricSources)) {
      if (aliases.includes(key)) return canonical;
    }
    return null;
  };

  const resolveArrayRegistersForKey = (canonicalKey: string) => {
    const aliases = arrayMetricSources[canonicalKey] || [canonicalKey];
    for (const alias of aliases) {
      const group = arrayGroup[alias] || [];
      if (group.length) return group;
    }
    return [];
  };

  const arrayConfigKeys = [
    'bess_aux_power',
    'bess_reactive_power_meter',
    'bess_energy_charge_year',
    'bess_energy_charge_day',
    'bess_device_status',
    'bess_alarm_status'
  ];

  const arraySuffixes = new Set();
  arrayConfigKeys.forEach(key => {
    const group = resolveArrayRegistersForKey(key);
    group.forEach(g => {
      const match = g.name.match(/_(\d*)$/);
      const suffix = match ? match[1] : "";
      arraySuffixes.add(suffix);
    });
  });

  if (!arraySuffixes.size) {
    registers.forEach((reg: any) => {
      const dk = String(reg?.dictionaryKey || '').toLowerCase();
      if (!dk.startsWith('bess_')) return;
      const match = String(reg?.name || '').match(/_(\d*)$/);
      const suffix = match ? match[1] : '';
      arraySuffixes.add(suffix);
    });
  }
  if (!arraySuffixes.size) {
    arraySuffixes.add('');
  }

  const bessUnits = Array.from(arraySuffixes)
    .sort((a, b) => (a === "" ? -1 : parseInt(a)) - (b === "" ? -1 : parseInt(b)))
    .map((suffix, i) => ({ id: `BESS ${i + 1}`, suffix }));

  const arrayValues = {};
  bessUnits.forEach(u => { arrayValues[u.id] = {}; });

  const normalizeStatusFlag = (raw: unknown): 0 | 1 | null => {
    if (raw === null || raw === undefined || raw === '') return null;
    if (typeof raw === 'boolean') return raw ? 1 : 0;
    const n = Number(raw);
    if (Number.isFinite(n)) return n >= 1 ? 1 : 0;
    const s = String(raw).trim().toLowerCase();
    if (['online', 'ok', 'true', 'alarm', 'fault', '1'].includes(s)) return 1;
    if (['offline', 'normal', 'false', '0'].includes(s)) return 0;
    return null;
  };

  const normalizeArrayValue = (configKey: string, raw: unknown): number | null => {
    if (raw === null || raw === undefined || raw === '') return null;
    if (configKey === 'bess_device_status' || configKey === 'bess_alarm_status') {
      return normalizeStatusFlag(raw);
    }
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };

  function updateArrayTable() {
    arrayTableBody.innerHTML = "";
    bessUnits.forEach(u => {
      const row = document.createElement("tr");
      const idTd = document.createElement("td");
      idTd.textContent = u.id;
      row.appendChild(idTd);

      arrayConfigKeys.forEach(configKey => {
        const group = resolveArrayRegistersForKey(configKey);
        const reg = group.find(g => g.name.endsWith(u.suffix));
        const raw = arrayValues[u.id][configKey] ?? null;
        const normalized = normalizeArrayValue(configKey, raw);
        const isDeviceStatus = configKey === 'bess_device_status';
        const isAlarmStatus = configKey === 'bess_alarm_status';
        const isStatus = isDeviceStatus || isAlarmStatus;
        const fallbackAlarm = isAlarmStatus && normalized === null
          ? normalizeArrayValue('bess_device_status', arrayValues[u.id]['bess_device_status'])
          : null;
        const effectiveStatus = normalized ?? fallbackAlarm;
        const display = (isStatus ? effectiveStatus : normalized) != null
          ? (
              isDeviceStatus
                ? (effectiveStatus === 1 ? 'Online' : 'Offline')
                : (isAlarmStatus ? (effectiveStatus === 1 ? 'Alarm' : 'OK') : (normalized as number).toFixed(2))
            )
          : "N/A";

        const td = document.createElement("td");
        td.className = "text-right w-100 bg-subcontainer";
        td.textContent = display;

        if (normalized != null && !isStatus) {
          applyThreshold(td, normalized as number, reg?.meta);
        } else if (isStatus) {
          const hasAlarm = isAlarmStatus && effectiveStatus === 1;
          const online = isDeviceStatus && effectiveStatus === 1;
          td.classList.add((hasAlarm || !online) ? "bg-error" : "bg-success");
        }
        row.appendChild(td);
      });
      arrayTableBody.appendChild(row);
    });
  }

  // Subscribe to array registers
  bessUnits.forEach(u => {
    arrayConfigKeys.forEach(configKey => {
      const group = resolveArrayRegistersForKey(configKey);
      const reg = group.find(g => g.name.endsWith(u.suffix));
      if (!reg) return;

      const register = registers.find(r => r.name === reg.name);
      if (!register) return;

      arrayValues[u.id][configKey] = null;

      const unsub = subscribeRegister(site._id, register.deviceId, register._id, p => {
        const value = normalizeArrayValue(configKey, p?.value ?? null);
        arrayValues[u.id][configKey] = value;
        updateArrayTable();
      });
      subscriptions.push(unsub);
    });
  });

  /* ========================================
     DYNAMIC METRICS TABLE
     ======================================== */
  const metrics = [
    { label: 'P', unit: 'kW', configKey: 'bess_active_power' },
    { label: 'Q', unit: 'kVAR', configKey: 'bess_reactive_power' },
    { label: 'SOC', unit: '%', configKey: 'bess_soc' },
    { label: 'SOH', unit: '%', configKey: 'bess_soh' },
    { label: 'Voltage', unit: 'V', configKey: 'bess_voltage' },
    { label: 'Current', unit: 'A', configKey: 'bess_current' },
  ];

  const allowedBases = new Set([
    ...metrics.map(m => m.configKey),
    'bess_available_banks',
    'bess_available_charge_power',
    'bess_available_discharge_power',
    'bess_temperature'
  ]);

  const bessIndices = new Set();
  let hasBase = false;

  registers.forEach(r => {
    const base = r.name.replace(/_\d+$/, '');
    if (allowedBases.has(base)) {
      const match = r.name.match(/_(\d+)$/);
      if (match) bessIndices.add(Number(match[1]));
      else hasBase = true;
    }
  });

  if (hasBase || bessIndices.size === 0) bessIndices.add(1);
  const bessList = Array.from(bessIndices).sort((a, b) => a - b);

  if (remoteControlSection) {
    const table = remoteControlSection.querySelector("table");
    if (table) {
      table.innerHTML = `
        <thead>
          <tr>
            <th colspan="2"></th>
            ${bessList.map(i => `<th>BESS ${i}</th>`).join('')}
          </tr>
        </thead>
        <tbody>
          ${metrics.map(m => `
            <tr data-metric="${m.configKey}">
              <td>${m.label}</td>
              <td class="text-right">${m.unit}</td>
              ${bessList.map(i => `
                <td class="text-right bg-subcontainer" data-bess="${i}" data-metric="${m.configKey}">0.0</td>
              `).join('')}
            </tr>
          `).join('')}
        </tbody>
      `;
    }
  }

  /* ========================================
     SUMMARY VALUES
     ======================================== */
  const summaryGroups = groupByDictionaryKey(registers);
  const summaryValues = {};

  // ========================================
  // 1. PRE-FILL ALL CURRENT VALUES (INSTANT LOAD!)
  // ========================================
  Object.entries(summaryGroups)
    .filter(([k]) => k.startsWith('bess_'))
    .forEach(([configKey, regs]) => {
      if (!summaryValues[configKey]) summaryValues[configKey] = {};
      regs.forEach(register => {
        if (register.value == null) return;
        const value = Number(register.value);
        if (!Number.isFinite(value)) return;
        summaryValues[configKey][register.name] = value;

        const canonicalArrayKey = resolveArrayCanonicalKey(configKey);
        if (canonicalArrayKey) {
          const match = register.name.match(/_(\d*)$/);
          const suffix = match ? match[1] : "";
          const unit = bessUnits.find(u => u.suffix === suffix);
          if (unit) arrayValues[unit.id][canonicalArrayKey] = value;
        }

        const idxMatch = register.name.match(/_(\d+)$/);
        const idx = idxMatch ? Number(idxMatch[1]) : 1;
        const cell = container.querySelector(`td[data-bess="${idx}"][data-metric="${configKey}"]`) as HTMLElement;
        if (cell) {
          cell.textContent = value.toFixed(1);
          applyThreshold(cell, value, register.meta);
        }
      });
    });

  // ========================================
  // 2. IMMEDIATELY RENDER TABLES WITH REAL DATA
  // ========================================
  requestAnimationFrame(() => {
    updateArrayTable();
    updateSummary();
  });

  void refreshBessAlarms();
  bessAlarmPollTimer = window.setInterval(() => {
    if (!container.isConnected) return;
    void refreshBessAlarms();
  }, 20_000);

  const summaryMapping: Record<string, string> = {
    soc: 'bess_soc',
    availablebanks: 'bess_available_banks',
    availablechargepower: 'bess_available_charge_power',
    availabledischargepower: 'bess_available_discharge_power',
    chargeenergy: 'bess_charge_energy',
    dischargeenergy: 'bess_discharge_energy'
  };

  const summaryAliases: Record<string, string[]> = {
    soc: ['bess_soc'],
    availablebanks: ['bess_available_banks'],
    availablechargepower: ['bess_available_charge_power'],
    availabledischargepower: ['bess_available_discharge_power'],
    chargeenergy: ['bess_charge_energy', 'bess_energy_charge_year'],
    dischargeenergy: ['bess_discharge_energy', 'bess_energy_discharge_year'],
    chargeenergytoday: ['bess_charge_energy_today', 'bess_energy_charge_day'],
    dischargeenergytoday: ['bess_discharge_energy_today', 'bess_energy_discharge_day'],
  };

  const summarySpec: SummaryThresholdSpec = {
    soc: { keys: ['BESS_SOC'], mode: 'avg' },
    availablebanks: { keys: ['BESS_AVAILABLE_BANKS'], mode: 'sum' },
    availablechargepower: { keys: ['BESS_AVAILABLE_CHARGE_POWER'], mode: 'sum' },
    availabledischargepower: { keys: ['BESS_AVAILABLE_DISCHARGE_POWER'], mode: 'sum' },
    chargeenergy: { keys: ['BESS_CHARGE_ENERGY'], mode: 'sum' },
    dischargeenergy: { keys: ['BESS_DISCHARGE_ENERGY'], mode: 'sum' },
  };

  const summaryValueMode: Record<string, 'sum' | 'avg'> = {
    soc: 'avg',
    availablebanks: 'sum',
    availablechargepower: 'sum',
    availabledischargepower: 'sum',
    chargeenergy: 'sum',
    dischargeenergy: 'sum',
    chargeenergytoday: 'sum',
    dischargeenergytoday: 'sum',
  };

  function resolveSummaryMetric(key: string): number | null {
    const aliases = summaryAliases[key] || [summaryMapping[key]];
    for (const alias of aliases) {
      if (!alias) continue;
      const group = summaryGroups[alias] || [];
      const value = computeAggregateFromMap(
        group as any[],
        summaryValues[alias] || {},
        summaryValueMode[key] || 'sum'
      );
      if (value !== null) return value;
    }
    return null;
  }

  function applyBessSummaryTextThreshold(el: HTMLElement, field: string, value: number) {
    const severity = getSummaryThresholdSeverity(field, value, registers as any[], summarySpec);

    el.style.backgroundColor = '';
    el.style.transition = 'color 0.3s ease';

    if (severity === 'critical') {
      el.style.color = '#ef4444';
      el.title = 'Critical threshold breach';
      return;
    }

    if (severity === 'high') {
      el.style.color = '#f97316';
      el.title = 'Threshold warning';
      return;
    }

    el.style.color = '#16a34a';
    el.title = 'Within threshold';
  }

  function updateSummary() {
    Object.keys(summaryMapping).forEach((elKey) => {
      const el = summaryElements[elKey] as HTMLElement;
      if (!el) return;
      const value = resolveSummaryMetric(elKey);
      if (value !== null) {
        el.textContent = value.toFixed(2);
        applyBessSummaryTextThreshold(el, elKey, value);
      } else {
        el.textContent = "N/A";
        clearThresholdIndicator(el);
      }
    });

    const chargeTotal = resolveSummaryMetric('chargeenergy');
    const dischargeTotal = resolveSummaryMetric('dischargeenergy');
    const chargeToday = resolveSummaryMetric('chargeenergytoday');
    const dischargeToday = resolveSummaryMetric('dischargeenergytoday');

    const chargeBasis = chargeToday ?? chargeTotal;
    const dischargeBasis = dischargeToday ?? dischargeTotal;

    const setDerived = (field: keyof typeof summaryElements, value: number | null) => {
      const el = summaryElements[field] as HTMLElement | null;
      if (!el) return;
      if (value === null || !Number.isFinite(value)) {
        el.textContent = 'N/A';
        clearThresholdIndicator(el);
        return;
      }
      el.textContent = value.toFixed(2);
      clearThresholdIndicator(el);
    };

    setDerived('chargeenergytoday', chargeToday);
    setDerived('dischargeenergytoday', dischargeToday);

    if (chargeBasis === null || dischargeBasis === null) {
      setDerived('netenergy', null);
      setDerived('energythroughput', null);
      setDerived('rtefficiency', null);
      return;
    }

    const netEnergy = dischargeBasis - chargeBasis;
    const throughput = dischargeBasis + chargeBasis;
    const rtEfficiency = chargeBasis > 0 ? (dischargeBasis / chargeBasis) * 100 : null;

    setDerived('netenergy', netEnergy);
    setDerived('energythroughput', throughput);
    setDerived('rtefficiency', rtEfficiency);
  }

  // Subscribe to all BESS metrics
  Object.entries(summaryGroups)
    .filter(([k]) => k.startsWith('bess_'))
    .forEach(([configKey, group]) => {
      if (!summaryValues[configKey]) summaryValues[configKey] = {};
      group.forEach(reg => {
        const unsub = subscribeRegister(site._id, reg.deviceId, reg._id, p => {
          const raw = p?.value ?? null;
          const value = Number(raw);
          const normalizedValue = Number.isFinite(value) ? value : null;
          summaryValues[configKey][reg.name] = normalizedValue;

          const canonicalArrayKey = resolveArrayCanonicalKey(configKey);
          if (canonicalArrayKey && normalizedValue !== null) {
            const match = reg.name.match(/_(\d*)$/);
            const suffix = match ? match[1] : "";
            const unit = bessUnits.find(u => u.suffix === suffix);
            if (unit) {
              arrayValues[unit.id][canonicalArrayKey] = normalizeArrayValue(canonicalArrayKey, normalizedValue);
              updateArrayTable();
            }
          }

          if (normalizedValue !== null) {
            const match = reg.name.match(/_(\d+)$/);
            const idx = match ? Number(match[1]) : 1;
            const cell = container.querySelector(`td[data-bess="${idx}"][data-metric="${configKey}"]`) as HTMLElement;
            if (cell) {
              cell.textContent = normalizedValue.toFixed(1);
              applyThreshold(cell, normalizedValue, reg.meta);
            }
          }

          updateSummary();
        });
        subscriptions.push(unsub);
      });
    });

  /* ========================================
     CHARTS
     ======================================== */
  const chartManager = new RechartsChartManager(site, registers);

  const setAvailabilityBindingsForPreset = (preset: string) => {
    if (preset === 'soc') {
      chartManager.bindRegisterToChart("pcs_avalability_comparison", [
        { assetType: "battery", metricKey: "soc", name: "SOC", line: { color: "#3b82f6" } },
        { assetType: "battery", metricKey: "averageTemperature", name: "Avg Temperature", yaxis: "y2", line: { color: "#f97316" } },
      ]);
      return;
    }

    chartManager.bindRegisterToChart("pcs_avalability_comparison", [
      { assetType: "battery", metricKey: "chargePower", name: "Available Charge Power", line: { color: "#17a2b8" } },
      { assetType: "battery", metricKey: "dischargePower", name: "Available Discharge Power", line: { color: "#20c997" } },
    ]);
  };

  chartManager.createChart("pcs_soc_comparison", [], {
    ...chartStyles.bessLayout,
    showControls: true,
    typeControlsInHeader: true,
    showDateRangeOnlyWhenFullscreen: true,
    showNavigatorOnlyWhenFullscreen: true,
    height: '100%',
  }, container);
  chartManager.bindRegisterToChart("pcs_soc_comparison", [
    { assetType: "battery", metricKey: "soc", name: "BESS SOC" }
  ]);

  chartManager.createChart("pcs_power_comparison", [], {
    ...chartStyles.pcsPowerLayout,
    showControls: true,
    typeControlsInHeader: true,
    showDateRangeOnlyWhenFullscreen: true,
    showNavigatorOnlyWhenFullscreen: true,
    height: '100%',
  }, container);
  chartManager.bindRegisterToChart("pcs_power_comparison", [
     {
      assetType: "battery",
      metricKey: "activePower",
      name: "Total BESS Power",
      line: { color: "#FFA500" }
    },
    {
      assetType: "battery",
      metricKey: "reactivePower",
      name: "Total BESS Reactive Power",
      line: { color: "#28A745" }
    }
  ]);

  chartManager.createChart("pcs_avalability_comparison", [], {
    ...chartStyles.pcsAvalabilityLayout,
    showControls: true,
    typeControlsInHeader: true,
    showDateRangeOnlyWhenFullscreen: true,
    showNavigatorOnlyWhenFullscreen: true,
    height: '100%',
  }, container);
  setAvailabilityBindingsForPreset('capacity');

  chartManager.createChart("average_temp_comparison", [], {
    ...chartStyles.averageTempLayout,
    showControls: true,
    typeControlsInHeader: true,
    showDateRangeOnlyWhenFullscreen: true,
    showNavigatorOnlyWhenFullscreen: true,
    height: '100%',
  }, container);
  chartManager.bindRegisterToChart("average_temp_comparison", [
    { assetType: "battery", metricKey: "averageTemperature", name: "Avg. BESS Temperature" }
  ]);

  let activeFullscreenPanel: HTMLElement | null = null;

  const applyChartFullscreenState = () => {
    const root = container.querySelector('.dashboard-bess-v2') as HTMLElement | null;
    if (!root) return;

    root.classList.toggle('dashboard-chart-focus-active', !!activeFullscreenPanel);
    root.querySelectorAll('.dashboard-panel').forEach((panel) => {
      panel.classList.remove('dashboard-chart-focus');
    });

    container.querySelectorAll('.full-screen-button').forEach((btn) => {
      btn.classList.remove('active');
      (btn as HTMLButtonElement).title = 'Fullscreen';
      (btn as HTMLButtonElement).innerHTML = '<span class="iconify" data-icon="mdi:fullscreen" style="font-size:14px;pointer-events:none;"></span>';
    });

    if (!activeFullscreenPanel) {
      window.dispatchEvent(new Event('resize'));
      return;
    }

    activeFullscreenPanel.classList.add('dashboard-chart-focus');

    const actionButton = activeFullscreenPanel.querySelector('.full-screen-button') as HTMLButtonElement | null;
    if (actionButton) {
      actionButton.classList.add('active');
      actionButton.title = 'Exit Fullscreen';
      actionButton.innerHTML = '<span class="iconify" data-icon="mdi:fullscreen-exit" style="font-size:14px;pointer-events:none;"></span>';
    }

    requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
  };

  const setChartFullscreen = (panel: HTMLElement) => {
    activeFullscreenPanel = activeFullscreenPanel === panel ? null : panel;
    applyChartFullscreenState();
  };

  const onDashboardKeydown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape' || !activeFullscreenPanel) return;
    activeFullscreenPanel = null;
    applyChartFullscreenState();
  };

  const wireDashboardControls = () => {
    const setBessSummaryTab = (tab: string) => {
      container.querySelectorAll('[data-chart-control="bess-summary-tab"]').forEach((chip) => {
        chip.classList.toggle('active', (chip as HTMLElement).dataset.tab === tab);
      });

      container.querySelectorAll('[data-bess-summary-item]').forEach((item) => {
        const isMatch = (item as HTMLElement).dataset.tab === tab;
        (item as HTMLElement).style.display = isMatch ? '' : 'none';
      });
    };

    container.addEventListener('click', (event) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      const fullscreenButton = target.closest('.full-screen-button') as HTMLButtonElement | null;
      if (fullscreenButton) {
        const panel = fullscreenButton.closest('.dashboard-panel') as HTMLElement | null;
        if (!panel) return;
        setChartFullscreen(panel);
        return;
      }

      const button = target.closest('.dashboard-v2-chart-actions .chip') as HTMLButtonElement | null;
      if (!button) return;

      const control = button.dataset.chartControl;
      if (control === 'bess-summary-tab') {
        setBessSummaryTab(button.dataset.tab || 'core');
        return;
      }

      if (button.dataset.action === 'view-bess-alarms') {
        navigateTo('/dashboard/alarms');
        return;
      }

      if (control !== 'preset') return;

      const actions = button.parentElement;
      if (actions) {
        actions.querySelectorAll('.chip[data-chart-control="preset"]').forEach((chip) => chip.classList.remove('active'));
      }
      button.classList.add('active');

      if ((button.dataset.chartId || '') === 'pcs_avalability_comparison') {
        setAvailabilityBindingsForPreset(button.dataset.preset || 'capacity');
      }
    });

    container.addEventListener('change', (event) => {
      const target = event.target as HTMLSelectElement | null;
      if (!target) return;

      const control = target.dataset.chartControl;
      if (control !== 'type') return;

      chartManager.setChartType(target.dataset.chartId || '', target.value);
    });

    setBessSummaryTab('core');
  };

  if (!hasWarmBessPage) {
    showInitialBessLoading();
    requestAnimationFrame(() => {
      clearInitialBessLoadingProgressively();
      sessionStorage.setItem(bessWarmKey, '1');
    });
  }

  wireDashboardControls();
  document.addEventListener('keydown', onDashboardKeydown);

  /* ========================================
     INIT & CLEANUP
     ======================================== */
  container.cleanup = () => {
    document.removeEventListener('keydown', onDashboardKeydown);
    if (bessAlarmPollTimer) {
      window.clearInterval(bessAlarmPollTimer);
      bessAlarmPollTimer = null;
    }
    initialBessRevealTimers.forEach((timerId) => window.clearTimeout(timerId));
    subscriptions.forEach(unsub => typeof unsub === "function" && unsub());
    chartManager.cleanup();
  };

  return container;
}